import { styled, useTheme } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { DateString } from 'routes/HumanResourcesModule/business/types';
import MultiStepModal, { ModalStepT } from 'ui/Modals/MultiStepModal';
import { HrSimulation } from 'routes/HumanResourcesModule/Simulation/helpers/types';
import { useTranslation } from 'react-i18next';
import { orgChartHrFormatter } from 'routes/HumanResourcesModule/components/HrDataGrid/helpers/orgChartHrColumnHelpers';
import { EnvelopePayComponentHypothesis } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Envelope/PayComponentHypothesis/helpers/types';
import { useEnvelopePayComponentHypothesisCRUDMutations } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Envelope/PayComponentHypothesis/helpers/hooks/payComponentHypothesis';
import EnvelopeHypothesisModalContent from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Envelope/EnvelopeHypothesisModalContent';
import { uncapitalize } from 'helpers/syntaxHelper';
import { TRACKABLE_EVENTS } from 'helpers/trackerHelpers';
import AddEnvelopePayComponentHypothesisModalFirstStep from './steps/AddEnvelopePayComponentHypothesisModalFirstStep';

const StyMultiStepModal = styled(MultiStepModal, {
    name: 'StyMultiStepModal',
})({
    '& .paper-static-size': {
        minHeight: '85vh',
        maxHeight: '85vh',
    },
});

const TRANSLATION_KEY = 'humanResources.simulation.envelope.creationModal';

type Props = {
    open: boolean;
    onClose: () => void;
    hrSimulation: HrSimulation;
    envelopePayComponentHypotheses: EnvelopePayComponentHypothesis[];
};

export default function AddEnvelopePayComponentHypothesisModal({
    open,
    onClose: onCloseProp,
    hrSimulation,
    envelopePayComponentHypotheses,
}: Props): React.ReactElement {
    const theme = useTheme();
    const { t } = useTranslation();

    const [selectedOrgChartHr, setSelectedOrgChartHr] = useState('');
    const [selectedPayComponent, setSelectedPayComponent] = useState('');

    const selectedOrgChartHrLabel = useMemo(() => {
        const selected = hrSimulation.orgChartHrs.find(
            (o) => o.softwareId === selectedOrgChartHr,
        );
        return orgChartHrFormatter({
            orgChartHrName: selected?.name ?? '',
            orgChartHrMapping: selected?.orgChartHrMapping?.title,
        });
    }, [selectedOrgChartHr, hrSimulation.orgChartHrs]);

    const [comment, setComment] = useState('');

    const [hasChanges, setHasChanges] = useState(false);

    const [valuesByDates, setValuesByDates] = useState<
        Record<DateString, number>
    >({});

    const onClose = () => {
        setSelectedOrgChartHr('');
        setSelectedPayComponent('');
        setComment('');
        setHasChanges(false);
        setValuesByDates({});
        onCloseProp();
    };

    const alreadyExistingHypothesis = useMemo(() => {
        if (!selectedOrgChartHr || !selectedPayComponent) {
            return undefined;
        }
        return envelopePayComponentHypotheses.find(
            (h) =>
                h.orgChartHrSoftwareId === selectedOrgChartHr &&
                h.payComponent === selectedPayComponent,
        );
    }, [
        envelopePayComponentHypotheses,
        selectedOrgChartHr,
        selectedPayComponent,
    ]);

    const { createHypothesis, editHypothesis } =
        useEnvelopePayComponentHypothesisCRUDMutations({
            simulationId: hrSimulation.id,
            trackEventKey:
                TRACKABLE_EVENTS.human_resources.simulation.payComponents
                    .validateNewComponentEnvelopeHypothesis,
            orgChartHrSoftwareId: selectedOrgChartHr,
            payComponent: selectedPayComponent,
            hypothesisId: alreadyExistingHypothesis?.id,
            onSettled: onClose,
        });

    const steps: ModalStepT[] = [
        {
            title: t('humanResources.simulation.addEnvelope.button'),
            node: (
                <AddEnvelopePayComponentHypothesisModalFirstStep
                    orgChartHrs={hrSimulation.orgChartHrs}
                    selectedOrgChartHr={selectedOrgChartHr}
                    setSelectedOrgChartHr={setSelectedOrgChartHr}
                    selectedPayComponent={selectedPayComponent}
                    setSelectedPayComponent={setSelectedPayComponent}
                />
            ),
            confirmAction: {
                enabled: Boolean(selectedOrgChartHr && selectedPayComponent),
            },
        },
        {
            title: t(
                'humanResources.simulation.envelope.creationModal.payComponent.title',
                {
                    payComponent: uncapitalize(selectedPayComponent),
                    orgChartHr: selectedOrgChartHrLabel,
                },
            ),
            node: (
                <EnvelopeHypothesisModalContent
                    hrSimulation={hrSimulation}
                    orgChartHrSoftwareId={selectedOrgChartHr}
                    envelopeFieldValue={selectedPayComponent}
                    envelopeField="payComponent"
                    onValuesByDatesChange={setValuesByDates}
                    setHasChanges={setHasChanges}
                    comment={comment}
                    setComment={setComment}
                    commentFieldLabel={t(
                        'humanResources.simulation.envelope.creationModal.comment',
                    )}
                    hypothesis={alreadyExistingHypothesis}
                    isUpdateEnabled
                    translationKey={TRANSLATION_KEY}
                />
            ),
            confirmAction: {
                enabled: hasChanges,
                label: t('common.validate'),
                onClick: () => {
                    if (alreadyExistingHypothesis !== undefined) {
                        editHypothesis({
                            comment,
                            valuesByDates,
                            oldValuesByDates:
                                alreadyExistingHypothesis.newValuesByDates,
                        });
                    } else {
                        createHypothesis({
                            comment,
                            valuesByDates,
                        });
                    }
                },
                shouldTriggerDefaultOnClose: false,
            },
            stepBackAction: {
                onClick: () => {
                    setComment('');
                    setValuesByDates({});
                    setHasChanges(false);
                },
            },
        },
    ];

    return (
        <StyMultiStepModal
            isOpen={open}
            onClose={onClose}
            maxWidth="lg"
            isSmallHeight
            steps={steps}
            PaperProps={{
                sx: { backgroundColor: theme.palette.lightGrey },
            }}
            shouldRunStepBackOnReset={false}
        />
    );
}
